// src/lib/video/embed.ts

function safeUrl(input: string) {
  try {
    return new URL(input)
  } catch {
    return null
  }
}

function getYouTubeId(input: string) {
  const u = safeUrl(input)
  if (!u) return ''

  const host = u.hostname.toLowerCase()
  const path = u.pathname

  // youtu.be/<id>
  if (host.includes('youtu.be')) {
    return path.split('/').filter(Boolean)[0] || ''
  }

  // youtube-nocookie.com/embed/<id>
  if (host.includes('youtube-nocookie.com')) {
    if (path.startsWith('/embed/')) {
      return path.split('/embed/')[1]?.split('/')[0] || ''
    }
  }

  // youtube.com/watch?v=<id>
  if (host.includes('youtube.com')) {
    const v = u.searchParams.get('v')
    if (v) return v

    // /embed/<id>
    if (path.startsWith('/embed/')) {
      return path.split('/embed/')[1]?.split('/')[0] || ''
    }

    // /shorts/<id>
    if (path.startsWith('/shorts/')) {
      return path.split('/shorts/')[1]?.split('/')[0] || ''
    }

    // /live/<id>
    if (path.startsWith('/live/')) {
      return path.split('/live/')[1]?.split('/')[0] || ''
    }

    // fallback raro tipo /v/<id>
    if (path.startsWith('/v/')) {
      return path.split('/v/')[1]?.split('/')[0] || ''
    }
  }

  return ''
}

function getVimeoId(input: string) {
  const u = safeUrl(input)
  if (!u) return ''

  const host = u.hostname.toLowerCase()
  const path = u.pathname

  if (!host.includes('vimeo.com')) return ''

  // player.vimeo.com/video/<id>
  let match = path.match(/\/video\/(\d+)/i)
  if (match?.[1]) return match[1]

  // vimeo.com/<id>
  match = path.match(/\/(\d+)(?:$|[/?#])/i)
  if (match?.[1]) return match[1]

  return ''
}

function getVimeoHash(input: string) {
  const u = safeUrl(input)
  if (!u) return ''

  const host = u.hostname.toLowerCase()
  if (!host.includes('vimeo.com')) return ''

  // Formato embed:
  // player.vimeo.com/video/123456?h=abcdef
  const queryHash = u.searchParams.get('h')
  if (
    queryHash &&
    /^[a-zA-Z0-9]+$/.test(queryHash)
  ) {
    return queryHash
  }

  const parts = u.pathname
    .split('/')
    .filter(Boolean)

  // Formato:
  // vimeo.com/123456/abcdef
  //
  // Busca el ID numérico y toma
  // el segmento inmediatamente posterior.
  const idIndex = parts.findIndex(
    (part) => /^\d+$/.test(part)
  )

  if (
    idIndex >= 0 &&
    parts[idIndex + 1] &&
    /^[a-zA-Z0-9]+$/.test(parts[idIndex + 1])
  ) {
    return parts[idIndex + 1]
  }

  return ''
}

function getLoomId(input: string) {
  const u = safeUrl(input)
  if (!u) return ''

  const host = u.hostname.toLowerCase()
  const path = u.pathname

  if (!host.includes('loom.com')) return ''

  // /share/<id> o /embed/<id>
  const match = path.match(/\/(?:share|embed)\/([a-zA-Z0-9]+)/i)
  return match?.[1] || ''
}

export function getEmbedUrl(url: string) {
  const raw = String(url || '').trim()
  if (!raw) return ''

  const u = safeUrl(raw)
  if (!u) return raw

  const host = u.hostname.toLowerCase()

  // YouTube
  if (
    host.includes('youtube.com') ||
    host.includes('youtu.be') ||
    host.includes('youtube-nocookie.com')
  ) {
    const id = getYouTubeId(raw)
    if (!id) return raw

    const start =
      u.searchParams.get('t') ||
      u.searchParams.get('start') ||
      ''

    const embed = new URL(`https://www.youtube.com/embed/${id}`)

    if (start) {
      const seconds = String(start).replace(/s$/i, '')
      embed.searchParams.set('start', seconds)
    }

    return embed.toString()
  }

  // Vimeo
  if (host.includes('vimeo.com')) {
    const id = getVimeoId(raw)
    if (!id) return raw

    const embed = new URL(
      `https://player.vimeo.com/video/${id}`
    )

    const hash = getVimeoHash(raw)

    if (hash) {
      embed.searchParams.set('h', hash)
    }

    return embed.toString()
  }

  // Loom
  if (host.includes('loom.com')) {
    const id = getLoomId(raw)
    if (!id) return raw

    return `https://www.loom.com/embed/${id}`
  }

  return raw
}

export function decodeHtmlEntities(html: string) {
  if (!html) return ''
  if (typeof document === 'undefined') return String(html || '')

  const txt = document.createElement('textarea')
  txt.innerHTML = html
  return txt.value
}

export function sanitizeEmbedHtml(html: string) {
  const s = String(html || '')

  return s
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '')
    .replace(/javascript:/gi, '')
}

export function extractIframeSrc(html: string) {
  const s = String(html || '')
  const m = s.match(/<iframe[^>]*\s+src=["']([^"']+)["'][^>]*>/i)
  return m?.[1] ? String(m[1]).trim() : ''
}